import commonMsg from 'features/charity-vetting/common-flow/messages';
import { CharityVettingUrlType } from 'features/charity-vetting/types/charityVettingTypes';
import { isValidPhoneNumber } from 'libphonenumber-js/max';
import { z } from 'zod';

import type { FormatMessage, PageSchemaFactory, PageSchemaMap } from './pageSchemaRegistry';

/**
 * Per-page Zod schemas for the common (country-agnostic) vetting steps.
 *
 * Each schema validates only the field(s) owned by its page; the rest of
 * `charityOnboarding` passes through untouched so navigation is gated solely on
 * the current page's inputs (the React Hook Form per-page model). Messages are
 * resolved via `formatMessage` to keep i18n parity with the old Yup schemas.
 */

const nonEmpty = (message: string) => z.string().trim().min(1, message);

const wrap = (charityOnboarding: z.ZodTypeAny) => z.looseObject({ charityOnboarding });

const hasActiveItem = (items: Array<{ active?: boolean }> | undefined) =>
  !!items?.some((item) => item.active);

// Validate the full international number (incl. the forced calling code) with
// libphonenumber-js, matching the live validity check in PhoneInput. This
// replaces a brittle fixed-digit-count regex that wrongly rejected valid numbers
// whose length differed from the hardcoded count.
const phoneSchema = (formatMessage: FormatMessage) =>
  z
    .string()
    .min(1, formatMessage(commonMsg.charityVettingPhoneNumberRequired))
    .refine((val) => isValidPhoneNumber(val), {
      message: formatMessage(commonMsg.charityVettingPhoneNumberValid),
    });

export const searchPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z.looseObject({
      search: nonEmpty(formatMessage(commonMsg.charityVettingSearchTerm)),
    }),
  );

export const manualContactPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z.looseObject({
      charityName: nonEmpty(formatMessage(commonMsg.charityVettingNameRequired)),
      address: nonEmpty(formatMessage(commonMsg.charityVettingAddressRequired)),
      city: nonEmpty(formatMessage(commonMsg.charityVettingCityRequired)),
      postalCode: nonEmpty(formatMessage(commonMsg.charityVettingPostalCodeRequired)),
      phoneNumber: phoneSchema(formatMessage),
      email: z
        .string()
        .min(1, formatMessage(commonMsg.charityVettingEmailRequired))
        .email(formatMessage(commonMsg.charityVettingEmailInvalid)),
    }),
  );

export const legalRepresentativePageSchema: PageSchemaFactory = () =>
  wrap(
    z.looseObject({
      isLegalRepresentative: z.boolean(),
    }),
  );

export const donationPreferencesPageSchema: PageSchemaFactory = () =>
  wrap(
    z.looseObject({
      donationPreferences: z.string(),
    }),
  );

export const acceptsAllFoodPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ acceptsAllFood: z.boolean() }));

export const acceptsAllNonFoodPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ acceptsAllNonFood: z.boolean() }));

export const acceptsAllFoodAndNonFoodPageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ acceptsAllFoodAndNonFood: z.boolean() }));

export const consumptionTypePageSchema: PageSchemaFactory = () =>
  wrap(z.looseObject({ consumptionType: z.string() }));

/**
 * The unwanted-food page is valid when at least one item is selected across
 * either the main list or the additional categories (mirrors the old Yup
 * cross-field `.test`). Modeled as an object-level refine since it spans two
 * sibling arrays.
 */
export const unwantedFoodPageSchema: PageSchemaFactory = () =>
  wrap(
    z
      .looseObject({
        unwantedFoodItems: z.array(z.looseObject({ active: z.boolean().optional() })),
        additionalUnwantedFoodCategories: z.array(
          z.looseObject({ active: z.boolean().optional() }),
        ),
      })
      .refine(
        (data) =>
          hasActiveItem(data.unwantedFoodItems) ||
          hasActiveItem(data.additionalUnwantedFoodCategories),
        // Message is never surfaced to the user — the snackbar comes from
        // getNotificationErrorMsg; this just marks the page invalid.
        { message: 'unwanted-food-required', path: ['unwantedFoodItems'] },
      ),
  );

export const socialAccountsPageSchema: PageSchemaFactory = (formatMessage) =>
  wrap(
    z.looseObject({
      socialMediaAccountLinks: z.array(
        z.looseObject({
          link: z
            .union([
              z.literal(''),
              z.url(formatMessage(commonMsg.charityVettingValidSocialMediaLink)),
            ])
            .nullable()
            .optional(),
        }),
      ),
    }),
  );

/** Country-agnostic page schemas, keyed by step name. */
export const commonPageSchemas: PageSchemaMap = {
  [CharityVettingUrlType.SEARCH]: searchPageSchema,
  [CharityVettingUrlType.CONTACT_DETAILS]: manualContactPageSchema,
  [CharityVettingUrlType.MANUAL_CONTACT_DETAILS]: manualContactPageSchema,
  [CharityVettingUrlType.LEGAL_REPRESENTATIVE]: legalRepresentativePageSchema,
  [CharityVettingUrlType.DONATION_PREFERENCES]: donationPreferencesPageSchema,
  [CharityVettingUrlType.ACCEPTS_ALL_FOODS]: acceptsAllFoodPageSchema,
  [CharityVettingUrlType.ACCEPTS_ALL_NON_FOOD]: acceptsAllNonFoodPageSchema,
  [CharityVettingUrlType.ACCEPTS_ALL_FOOD_AND_NON_FOOD]: acceptsAllFoodAndNonFoodPageSchema,
  [CharityVettingUrlType.DONATION_CONSUMPTION_TYPE]: consumptionTypePageSchema,
  [CharityVettingUrlType.UNWANTED_FOOD]: unwantedFoodPageSchema,
  [CharityVettingUrlType.SOCIAL_ACCOUNTS]: socialAccountsPageSchema,
};
